import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Rocket, FileText, Zap, Search, ChevronDown, AlertTriangle, RefreshCw } from 'lucide-react';
import { useDockerHub } from '../hooks/useDockerHub';
import * as imageService from '../services/imageService';
import * as clusterService from '../services/clusterService';
import orgService from '../services/organizationService';

const formatCents = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// Explains where a price came from, so an unexpected number is traceable
// back to the admin setting that produced it.
const PRICE_SOURCE_LABEL = {
  image_fixed: 'model rate',
  user_fixed: 'your custom rate',
  user_markup: 'your custom markup',
  config_fixed: 'fixed rate',
  config_markup: 'GPU markup',
  global_markup: 'standard rate',
};

const LaunchClusterModal = ({ onClose, onLaunch, isAdmin }) => {
  const [clusterName, setClusterName] = useState('');
  const [mode, setMode] = useState('simple'); // simple or advanced
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Docker Hub integration
  const { isAuthenticated: isDockerHubAuth, getRepositories, checkAuthState } = useDockerHub();
  const [dockerImages, setDockerImages] = useState([]);
  const [selectedDockerImage, setSelectedDockerImage] = useState('');
  const [loadingImages, setLoadingImages] = useState(false);

  // Curated images
  const [curatedImages, setCuratedImages] = useState([]);
  const [loadingCurated, setLoadingCurated] = useState(false);

  // Visibility
  const [visibility, setVisibility] = useState('private');

  // Cloud provider (admin only)
  const [cloud, setCloud] = useState('');

  // Simple mode state
  const [providerType, setProviderType] = useState('both'); // datacenter, community, both
  // Launching into an org bills its shared pool instead of the personal
  // balance. Defaults to the last-used context so a team member does not have
  // to re-pick it on every launch; null means personal.
  const [organizations, setOrganizations] = useState([]);
  const [organizationId, setOrganizationId] = useState(orgService.getActiveOrganizationId());
  const [region, setRegion] = useState('');
  const [minCpuCores, setMinCpuCores] = useState(4);
  const [minRamGb, setMinRamGb] = useState(8);
  const [instanceType, setInstanceType] = useState('both'); // spot, on-demand, both
  const [accelerators, setAccelerators] = useState('');
  const [numNodes, setNumNodes] = useState(1);
  const [diskSize, setDiskSize] = useState(100);
  const [setup, setSetup] = useState('');
  const [run, setRun] = useState('');

  // Autoscaling (UI mockup — not functional yet)
  const [autoscaleEnabled, setAutoscaleEnabled] = useState(false);
  const [autoscaleMinNodes, setAutoscaleMinNodes] = useState(1);
  const [autoscaleMaxNodes, setAutoscaleMaxNodes] = useState(3);
  // 'nats_inflight' is the implemented strategy (relay-tracked outstanding
  // requests). The dropdown keeps other entries for future algorithms.
  const [autoscaleStrategy, setAutoscaleStrategy] = useState('nats_inflight');
  // Target in-flight requests per replica for the nats_inflight strategy.
  const [autoscaleTarget, setAutoscaleTarget] = useState(4);
  const [autoscaleCooldown, setAutoscaleCooldown] = useState(300);
  // Exposed ports (frp tunnel): [{ port, protocol, name, auth }]. Lets users
  // reach a Gradio/ComfyUI UI or raw tcp/udp on a worker with no public IP.
  const [exposedPorts, setExposedPorts] = useState([]);
  // Interactive app mode (predict-less): run a web UI instead of an inference
  // model. Uses the Run Commands as the app launch command.
  const [appMode, setAppMode] = useState(false);

  // GPU dropdown state
  const [acceleratorList, setAcceleratorList] = useState([]);
  const [acceleratorMeta, setAcceleratorMeta] = useState({});
  const [acceleratorsLoading, setAcceleratorsLoading] = useState(true);
  const [acceleratorsError, setAcceleratorsError] = useState(null);
  const [acceleratorsUnconfigured, setAcceleratorsUnconfigured] = useState(false);
  const [gpuSearch, setGpuSearch] = useState('');
  const [gpuDropdownOpen, setGpuDropdownOpen] = useState(false);
  const gpuDropdownRef = useRef(null);

  // Live cost estimate
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateError, setEstimateError] = useState(null);
  const [balanceCents, setBalanceCents] = useState(null);
  const [hoursRemaining, setHoursRemaining] = useState(null);

  // Advanced mode state
  const [taskYaml, setTaskYaml] = useState('');

  // Check auth state and fetch curated images when modal opens
  useEffect(() => {
    if (checkAuthState) checkAuthState();
    fetchCuratedImages();
  }, [checkAuthState]);

  /**
   * Load the accelerators this account may launch, with their prices.
   *
   * A failure here must NOT fall back to the full catalog: that would offer
   * hardware the admin has not allowed and quote no price for it. Only an
   * explicitly unconfigured allowlist opens the full list, and it says so.
   */
  const fetchAccelerators = useCallback(async () => {
    setAcceleratorsLoading(true);
    setAcceleratorsError(null);
    try {
      // When a specific cloud is chosen, restrict the list (and pricing) to
      // accelerators that provider actually offers. Empty cloud = "Auto" = all.
      const data = await imageService.listAvailableAccelerators(providerType, instanceType === 'spot', cloud);
      if (!data.success) throw new Error(data.error || 'Failed to load accelerators');

      const entries = (data.accelerators || []).map(a => (typeof a === 'string' ? { name: a } : a));
      const metaByName = {};
      for (const entry of entries) metaByName[entry.name] = entry;

      setAcceleratorMeta(metaByName);
      setAcceleratorsUnconfigured(!!data.unconfigured);

      if (entries.length > 0) {
        setAcceleratorList(entries.map(e => e.name));
      } else if (data.unconfigured) {
        // No allowlist configured and the catalog gave us nothing — fall back
        // to the known GPU names so the form remains usable during setup.
        const all = await imageService.listAccelerators();
        setAcceleratorList(all.success ? (all.accelerators || []) : []);
      } else {
        setAcceleratorList([]);
      }
    } catch (err) {
      setAcceleratorList([]);
      setAcceleratorMeta({});
      setAcceleratorsError(err.message || 'Failed to load accelerators');
    } finally {
      setAcceleratorsLoading(false);
    }
  }, [providerType, instanceType, cloud]);

  useEffect(() => {
    orgService.listOrganizations()
      .then((d) => setOrganizations(d.organizations || []))
      .catch(() => setOrganizations([]));  // not fatal: personal launch still works
  }, []);

  useEffect(() => { fetchAccelerators(); }, [fetchAccelerators]);

  // Switching provider type can revoke the current selection — drop it rather
  // than submitting a GPU the launch endpoint will reject.
  useEffect(() => {
    const selected = accelerators.split(':')[0];
    if (!selected || acceleratorsLoading || acceleratorsError) return;
    if (acceleratorList.length > 0 && !acceleratorList.includes(selected)) {
      setAccelerators('');
    }
  }, [acceleratorList, acceleratorsLoading, acceleratorsError, accelerators]);

  // The GPU currently requested, whichever mode the form is in. Advanced mode
  // is priced from the YAML itself so the estimate follows what will be sent.
  const selectedGpu = mode === 'simple'
    ? accelerators.split(':')[0]
    : (taskYaml.match(/accelerators:\s*\{?\s*["']?([A-Za-z][^:"'\}\n]*[A-Za-z0-9])/i)?.[1] || '');

  // Live cost estimate. Debounced — the count and node inputs fire on every
  // keystroke, and each estimate hits the provider catalog.
  useEffect(() => {
    // A CPU-only deployment is a real configuration, not an unfinished form:
    // bailing here meant no estimate was ever requested, so the panel stayed
    // empty and the cluster then ran unpriced. Only give up in YAML mode with
    // nothing written yet.
    if (!selectedGpu && mode !== 'simple' && !taskYaml.trim()) {
      setEstimate(null);
      setEstimateError(null);
      return;
    }

    let cancelled = false;
    setEstimateLoading(true);

    const timer = setTimeout(async () => {
      try {
        const shape = mode === 'simple'
          ? {
              ...(selectedGpu ? {
                accelerator: accelerators.split(':')[0],
                acceleratorCount: parseInt(accelerators.split(':')[1] || '1', 10) || 1,
              } : {}),
              // Sent always: for a CPU-only shape these are the only
              // description of the machine, and SkyPilot resolves them with
              // the same logic the launch uses.
              cpus: String(minCpuCores),
              memory: String(minRamGb),
              numNodes: numNodes || 1,
              regionHint: region || undefined,
              useSpot: instanceType === 'spot',
            }
          : { taskYaml };

        const data = await clusterService.estimateClusterCost({
          ...shape,
          cloudHint: cloud || undefined,
          providerType,
          dockerImage: selectedDockerImage || undefined,
        });
        if (cancelled) return;
        setEstimate(data.estimate);
        setBalanceCents(data.balance_cents);
        setHoursRemaining(data.hours_remaining);
        setEstimateError(data.estimate ? null : (
          data.reason === 'no_catalog_data'
            ? 'No live pricing for this configuration yet.'
            : null
        ));
      } catch (err) {
        if (cancelled) return;
        setEstimate(null);
        setEstimateError(err.message || 'Could not estimate cost');
      } finally {
        if (!cancelled) setEstimateLoading(false);
      }
    }, 400);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [selectedGpu, mode, taskYaml, accelerators, numNodes, cloud, region, providerType, instanceType, selectedDockerImage]);

  // Close GPU dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (gpuDropdownRef.current && !gpuDropdownRef.current.contains(e.target)) {
        setGpuDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchCuratedImages = async () => {
    setLoadingCurated(true);
    try {
      const data = await imageService.listCuratedImages();
      if (data.success) setCuratedImages((data.images || []).filter(img => img.tcf_status === 'completed'));
    } catch (err) {
      console.error('Failed to fetch curated images:', err);
    } finally {
      setLoadingCurated(false);
    }
  };

  // Fetch Docker images from user's account
  useEffect(() => {
    const fetchDockerImages = async () => {
      if (isDockerHubAuth) {
        setLoadingImages(true);
        try {
          const result = await getRepositories();
          if (result.success && result.repositories) {
            setDockerImages(result.repositories);
          }
        } catch (err) {
          console.error('Failed to fetch Docker images:', err);
        } finally {
          setLoadingImages(false);
        }
      } else {
        // Reset images if not authenticated
        setDockerImages([]);
        setSelectedDockerImage('');
      }
    };

    fetchDockerImages();
  }, [isDockerHubAuth, getRepositories]);

  // Template options
  const templates = {
    empty: '',
    pytorch_training: `# PyTorch Training Cluster
resources:
  cpus: "8+"
  memory: "32+"
  use_spot: true
  accelerators: V100:1
  disk_size: 100

setup: |
  pip install torch torchvision
  pip install wandb

run: |
  python train.py --epochs 100

workdir: ~/project`,

    llm_inference: `# LLM Inference Cluster
resources:
  cpus: "16+"
  memory: "64+"
  use_spot: false
  accelerators: A100:1
  disk_size: 200

setup: |
  pip install transformers accelerate
  pip install torch

run: |
  python serve.py --model meta-llama/Llama-2-7b-hf

workdir: ~/inference`,

    distributed_training: `# Distributed Training (Multi-Node)
resources:
  cpus: "32+"
  memory: "128+"
  use_spot: true
  accelerators: A100:8
  disk_size: 500

num_nodes: 4

setup: |
  pip install torch torchvision
  pip install deepspeed
  pip install transformers

run: |
  torchrun --nproc_per_node=8 train.py

workdir: ~/distributed`,

    jupyter_notebook: `# Jupyter Notebook Server
resources:
  cpus: "4+"
  memory: "16+"
  accelerators: T4:1
  disk_size: 100

setup: |
  pip install jupyter jupyterlab
  pip install torch pandas numpy matplotlib

run: |
  jupyter lab --ip=0.0.0.0 --port=8888 --no-browser

workdir: ~/notebooks`
  };

  /**
   * Generate YAML from simple form
   */
  const generateYamlFromForm = () => {
    let yaml = `resources:\n`;
    yaml += `  cpus: "${minCpuCores}+"\n`;
    yaml += `  memory: "${minRamGb}+"\n`;

    if (instanceType === 'spot') yaml += `  use_spot: true\n`;
    else if (instanceType === 'on-demand') yaml += `  use_spot: false\n`;

    if (region) yaml += `  region: ${region}\n`;
    if (accelerators) yaml += `  accelerators: ${accelerators}\n`;
    yaml += `  disk_size: ${diskSize}\n`;

    if (numNodes > 1) {
      yaml += `\nnum_nodes: ${numNodes}\n`;
    }

    if (setup) {
      yaml += `\nsetup: |\n`;
      setup.split('\n').forEach(line => {
        yaml += `  ${line}\n`;
      });
    }

    if (run) {
      yaml += `\nrun: |\n`;
      run.split('\n').forEach(line => {
        yaml += `  ${line}\n`;
      });
    }

    return yaml;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!clusterName.trim()) {
      setError('Cluster name is required');
      return;
    }

    if (!/^[a-z0-9-]+$/.test(clusterName)) {
      setError('Cluster name must contain only lowercase letters, numbers, and hyphens');
      return;
    }

    const finalYaml = mode === 'simple' ? generateYamlFromForm() : taskYaml;

    if (!finalYaml.trim()) {
      setError('Task configuration is required');
      return;
    }

    setLoading(true);

    const launchParams = {
      clusterName: clusterName.trim(),
      taskYaml: finalYaml,
      providerType,
      regionHint: region || undefined,
      cloudHint: cloud || undefined,
      visibility,
      dockerImage: selectedDockerImage || undefined
    };

    if (autoscaleEnabled) {
      // Enabling autoscaling launches a SkyServe service (not a single
      // cluster). The worker turns this into a `service.replica_policy`.
      launchParams.autoscalingConfig = {
        strategy: autoscaleStrategy,
        min_replicas: autoscaleMinNodes,
        max_replicas: autoscaleMaxNodes,
        target_per_replica: autoscaleTarget,
        cooldown_seconds: autoscaleCooldown,
        // Back-compat aliases for older readers.
        min_nodes: autoscaleMinNodes,
        max_nodes: autoscaleMaxNodes,
      };
    }

    const validPorts = exposedPorts.filter(p => p.port && String(p.port).trim());
    if (validPorts.length > 0) {
      launchParams.exposedPorts = validPorts.map(p => ({
        port: parseInt(p.port, 10),
        protocol: p.protocol || 'http',
        name: (p.name || '').trim() || `p${p.port}`,
        ...(p.auth ? { auth: p.auth.trim() } : {}),
      }));
    }

    if (appMode) {
      launchParams.appMode = true;
      launchParams.runCommand = run;
      if (!launchParams.autostop) launchParams.autostop = 90; // idle autostop (min)
    }

    const result = await onLaunch(launchParams);

    setLoading(false);

    if (result && !result.success) {
      setError(result.error || 'Failed to launch cluster');
    }
  };

  /**
   * Load a template
   */
  const loadTemplate = (templateKey) => {
    setTaskYaml(templates[templateKey]);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3 text-white">
            <Rocket size={24} />
            <h2 className="text-xl font-bold">Launch New Cluster</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="px-6 py-4 border-b border-gray-200 flex space-x-2">
          <button
            onClick={() => setMode('simple')}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
              mode === 'simple'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Zap size={16} className="inline mr-2" />
            Simple Mode
          </button>
          <button
            onClick={() => setMode('advanced')}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
              mode === 'advanced'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FileText size={16} className="inline mr-2" />
            Advanced (YAML)
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-4">
            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Cluster Name - Always visible */}
            <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Bill to</label>
                <select
                  value={organizationId ?? ''}
                  onChange={(e) => setOrganizationId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
                >
                  <option value="">Personal — my own credit</option>
                  {organizations.map((o) => (
                    <option key={o.id} value={o.id}>{o.name} — shared organization credit</option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-400 mt-1">
                  An organization launch is charged to that organization's pool and
                  is visible to every member, not just you.
                </p>
              </div>
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cluster Name *
              </label>
              <input
                type="text"
                value={clusterName}
                onChange={(e) => setClusterName(e.target.value.toLowerCase())}
                placeholder="my-training-cluster"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Use lowercase letters, numbers, and hyphens only
              </p>
            </div>

            {/* Visibility */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visibility
              </label>
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="private">Private (only you)</option>
                <option value="public">Public (all users)</option>
                <option value="semi_private">Semi-Private (shared with specific users)</option>
              </select>
            </div>

            {/* Curated Images (TCF Ready only) */}
            {curatedImages.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Zap size={14} className="inline mr-1" />
                  Available Models
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                  {curatedImages.map(img => (
                    <button
                      key={img.id}
                      type="button"
                      onClick={() => {
                        setSelectedDockerImage(img.tcf_image || img.docker_image);
                        setRun('/TCF/start.sh');
                        setSetup('');
                        // Apply default resources from curated image
                        const res = img.default_resources;
                        if (res) {
                          if (res.cpus) setMinCpuCores(res.cpus);
                          if (res.memory) setMinRamGb(res.memory);
                          if (res.accelerator) setAccelerators(`${res.accelerator}:${res.accelerator_count || 1}`);
                          if (res.disk_size) setDiskSize(res.disk_size);
                        }
                        // Pre-fill exposed ports from the curated image defaults
                        if (Array.isArray(img.exposed_ports) && img.exposed_ports.length > 0) {
                          setExposedPorts(img.exposed_ports.map(p => ({
                            port: p.port ?? '', protocol: p.protocol || 'http', name: p.name || '', auth: p.auth || '',
                          })));
                        }
                      }}
                      className={`text-left p-2 rounded-lg border text-sm transition-colors ${
                        selectedDockerImage === (img.tcf_image || img.docker_image)
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-1">
                        <div className="font-medium text-gray-900 truncate">{img.name}</div>
                        <span className="px-1 py-0.5 rounded text-[9px] font-semibold bg-green-100 text-green-700 whitespace-nowrap">TCF Ready</span>
                      </div>
                      <div className="text-xs text-gray-500 font-mono truncate">{img.tcf_image || img.docker_image}</div>
                    </button>
                  ))}
                </div>
                {selectedDockerImage && curatedImages.some(img => (img.tcf_image || img.docker_image) === selectedDockerImage) && (
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-green-600">Selected: {selectedDockerImage}</p>
                    <button type="button" onClick={() => { setSelectedDockerImage(''); setRun(''); setSetup(''); setMinCpuCores(4); setMinRamGb(8); setAccelerators(''); setDiskSize(100); setExposedPorts([]); }} className="text-xs text-gray-400 hover:text-gray-600">Clear</button>
                  </div>
                )}
              </div>
            )}
            {loadingCurated && (
              <p className="text-xs text-gray-500">Loading curated images...</p>
            )}

            {/* Docker Image Selection - Always visible */}
            {isDockerHubAuth && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Docker Image
                </label>
                <select
                  value={selectedDockerImage}
                  onChange={(e) => setSelectedDockerImage(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={loadingImages}
                >
                  <option value="">-- Select Docker Image (Optional) --</option>
                  {dockerImages.map((image) => (
                    <option key={image.namespace + '/' + image.name} value={image.namespace + '/' + image.name}>
                      {image.namespace}/{image.name}
                    </option>
                  ))}
                </select>
                {loadingImages && (
                  <p className="text-xs text-gray-500 mt-1">Loading your Docker images...</p>
                )}
                {!loadingImages && dockerImages.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">No Docker images found in your account</p>
                )}
                {selectedDockerImage && (
                  <p className="text-xs text-blue-600 mt-1">
                    Selected: {selectedDockerImage}
                  </p>
                )}
              </div>
            )}

            {/* Simple Mode Form */}
            {mode === 'simple' && (
              <div className="space-y-4">
                {/* Admin: Cloud Provider */}
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cloud Provider
                    </label>
                    <select
                      value={cloud}
                      onChange={(e) => setCloud(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      <option value="">Auto (best price)</option>
                      <option value="aws">AWS</option>
                      <option value="runpod">RunPod</option>
                      <option value="vast">Vast.ai</option>
                      <option value="lambda_cloud">Lambda Cloud</option>
                      <option value="paperspace">Paperspace</option>
                      <option value="gcp">Google Cloud</option>
                      <option value="azure">Microsoft Azure</option>
                      <option value="ssh">SSH (On-Premise)</option>
                    </select>
                  </div>
                )}

                {cloud !== 'ssh' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Provider Type *
                    </label>
                    <select
                      value={providerType}
                      onChange={(e) => setProviderType(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      required
                    >
                      <option value="datacenter">Only Datacenter</option>
                      <option value="community">Only Community</option>
                      <option value="both">Both</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Instance Type *
                    </label>
                    <select
                      value={instanceType}
                      onChange={(e) => setInstanceType(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      required
                    >
                      <option value="spot">Only Spot</option>
                      <option value="on-demand">Only On-Demand</option>
                      <option value="both">Both</option>
                    </select>
                  </div>
                </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Minimum CPU Cores *
                    </label>
                    <input
                      type="number"
                      value={minCpuCores}
                      onChange={(e) => setMinCpuCores(parseInt(e.target.value))}
                      min="1"
                      max="128"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Minimum RAM (GB) *
                    </label>
                    <input
                      type="number"
                      value={minRamGb}
                      onChange={(e) => setMinRamGb(parseInt(e.target.value))}
                      min="1"
                      max="1024"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Region
                    </label>
                    <input
                      type="text"
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                      placeholder="us-west-2 (optional)"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Accelerators (GPUs)
                    </label>
                    <div className="flex space-x-2">
                      <div ref={gpuDropdownRef} className="relative flex-1">
                        <div
                          onClick={() => setGpuDropdownOpen(!gpuDropdownOpen)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 cursor-pointer flex items-center justify-between hover:border-gray-400"
                        >
                          <span className={accelerators.split(':')[0] ? 'text-gray-900' : 'text-gray-400'}>
                            {accelerators.split(':')[0] || 'Select GPU...'}
                          </span>
                          <ChevronDown size={14} className="text-gray-400" />
                        </div>

                        {gpuDropdownOpen && (
                          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-hidden">
                            <div className="p-2 border-b border-gray-100">
                              <div className="flex items-center border border-gray-300 rounded px-2 py-1">
                                <Search size={12} className="text-gray-400 mr-1" />
                                <input
                                  type="text"
                                  value={gpuSearch}
                                  onChange={(e) => setGpuSearch(e.target.value)}
                                  placeholder="Search GPUs..."
                                  className="w-full text-sm outline-none"
                                  autoFocus
                                />
                              </div>
                            </div>
                            <div className="max-h-36 overflow-y-auto">
                              {acceleratorsLoading ? (
                                <div className="p-2 text-xs text-gray-500 text-center">Loading...</div>
                              ) : acceleratorsError ? (
                                <div className="p-3 text-xs text-red-600 text-center space-y-2">
                                  <p>{acceleratorsError}</p>
                                  <button
                                    type="button"
                                    onClick={fetchAccelerators}
                                    className="inline-flex items-center space-x-1 text-purple-600 hover:text-purple-800"
                                  >
                                    <RefreshCw size={11} />
                                    <span>Retry</span>
                                  </button>
                                </div>
                              ) : acceleratorList.length === 0 ? (
                                <div className="p-3 text-xs text-gray-500 text-center">
                                  No GPUs are enabled for your account. Contact an administrator.
                                </div>
                              ) : (
                                <>
                                  {accelerators.split(':')[0] && (
                                    <button
                                      type="button"
                                      onClick={() => { setAccelerators(''); setGpuDropdownOpen(false); setGpuSearch(''); }}
                                      className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:bg-gray-50 italic"
                                    >
                                      Clear selection
                                    </button>
                                  )}
                                  {acceleratorList
                                    .filter(g => g.toLowerCase().includes(gpuSearch.toLowerCase()))
                                    .map(gpu => {
                                      const meta = acceleratorMeta[gpu];
                                      return (
                                        <button
                                          key={gpu}
                                          type="button"
                                          onClick={() => {
                                            const count = accelerators.split(':')[1] || '1';
                                            setAccelerators(`${gpu}:${count}`);
                                            setGpuDropdownOpen(false);
                                            setGpuSearch('');
                                          }}
                                          className={`w-full text-left px-3 py-1.5 text-sm hover:bg-purple-50 flex justify-between items-center ${
                                            accelerators.split(':')[0] === gpu ? 'bg-purple-50 text-purple-700 font-medium' : 'text-gray-700'
                                          }`}
                                        >
                                          <span>{gpu}</span>
                                          {meta?.price_cents_per_hour != null ? (
                                            <span className="text-xs text-gray-500 ml-2 font-mono">
                                              from {formatCents(meta.price_cents_per_hour)}/hr
                                            </span>
                                          ) : (
                                            <span className="text-xs text-gray-300 ml-2">no price</span>
                                          )}
                                        </button>
                                      );
                                    })
                                  }
                                  {acceleratorList.filter(g => g.toLowerCase().includes(gpuSearch.toLowerCase())).length === 0 && (
                                    <div className="p-2 text-xs text-gray-500 text-center">No matches</div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      {accelerators.split(':')[0] && (
                        <input
                          type="number"
                          min="1"
                          max="8"
                          value={accelerators.split(':')[1] || '1'}
                          onChange={(e) => {
                            const gpu = accelerators.split(':')[0];
                            setAccelerators(`${gpu}:${e.target.value || '1'}`);
                          }}
                          className="w-16 border border-gray-300 rounded-lg px-2 py-2 text-center"
                          title="GPU count"
                        />
                      )}
                    </div>
                    {acceleratorsUnconfigured && (
                      <p className="text-xs text-amber-600 mt-1">
                        No GPU allowlist configured — showing all catalog hardware.
                      </p>
                    )}
                    {acceleratorsError && (
                      <p className="text-xs text-red-600 mt-1">{acceleratorsError}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {autoscaleEnabled ? 'Min Nodes' : 'Num Nodes'}
                    </label>
                    <input
                      type="number"
                      value={autoscaleEnabled ? autoscaleMinNodes : numNodes}
                      onChange={(e) => {
                        const v = parseInt(e.target.value) || 1;
                        if (autoscaleEnabled) {
                          setAutoscaleMinNodes(v);
                          setNumNodes(v);
                          if (v > autoscaleMaxNodes) setAutoscaleMaxNodes(v);
                        } else {
                          setNumNodes(v);
                        }
                      }}
                      min="1"
                      max="64"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Disk Size (GB)
                    </label>
                    <input
                      type="number"
                      value={diskSize}
                      onChange={(e) => setDiskSize(parseInt(e.target.value))}
                      min="50"
                      max="2000"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                {/* Autoscaling toggle + config */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setAutoscaleEnabled(v => {
                        if (!v) {
                          setAutoscaleMinNodes(numNodes || 1);
                          setAutoscaleMaxNodes(Math.max((numNodes || 1) + 2, 3));
                        } else {
                          setNumNodes(autoscaleMinNodes);
                        }
                        return !v;
                      });
                    }}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-700">Autoscaling</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">Serve</span>
                    </div>
                    <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      autoscaleEnabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}>
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        autoscaleEnabled ? 'translate-x-4' : 'translate-x-0.5'
                      }`} />
                    </div>
                  </button>

                  {autoscaleEnabled && (
                    <div className="px-4 py-3 space-y-3 border-t border-gray-200 bg-purple-50/30">
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Min Replicas</label>
                          <input
                            type="number"
                            value={autoscaleMinNodes}
                            onChange={(e) => {
                              const v = Math.max(0, parseInt(e.target.value) || 0);
                              setAutoscaleMinNodes(v);
                              if (v > autoscaleMaxNodes) setAutoscaleMaxNodes(v);
                            }}
                            min="0"
                            max="64"
                            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Max Replicas</label>
                          <input
                            type="number"
                            value={autoscaleMaxNodes}
                            onChange={(e) => {
                              const v = parseInt(e.target.value) || autoscaleMinNodes;
                              setAutoscaleMaxNodes(Math.max(v, autoscaleMinNodes));
                            }}
                            min={autoscaleMinNodes}
                            max="64"
                            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Cooldown</label>
                          <select
                            value={autoscaleCooldown}
                            onChange={(e) => setAutoscaleCooldown(parseInt(e.target.value))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
                          >
                            <option value={60}>1 minute</option>
                            <option value={300}>5 minutes</option>
                            <option value={600}>10 minutes</option>
                            <option value={900}>15 minutes</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Scaling Strategy</label>
                        <select
                          value={autoscaleStrategy}
                          onChange={(e) => setAutoscaleStrategy(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
                        >
                          <option value="nats_inflight">In-Flight Requests — scale on outstanding requests per replica</option>
                          <option value="gpu_utilization" disabled>GPU Utilization — coming soon</option>
                          <option value="queue_depth" disabled>Queue Depth — coming soon</option>
                          <option value="request_latency" disabled>Request Latency — coming soon</option>
                        </select>
                      </div>
                      {autoscaleStrategy === 'nats_inflight' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Target in-flight requests / replica</label>
                          <input
                            type="number"
                            value={autoscaleTarget}
                            onChange={(e) => setAutoscaleTarget(Math.max(1, parseInt(e.target.value) || 1))}
                            min="1"
                            max="1024"
                            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                          />
                        </div>
                      )}
                      <p className="text-[11px] text-gray-500">
                        Enabling autoscaling launches this as a <span className="font-medium">replicated service</span> ({autoscaleMinNodes}–{autoscaleMaxNodes} replicas). The controller scales replicas to keep ~{autoscaleTarget} in-flight request{autoscaleTarget !== 1 ? 's' : ''} per replica, reading live load from the request relay.{autoscaleMinNodes === 0 ? ' Scales to zero when idle.' : ''}
                      </p>
                    </div>
                  )}
                </div>

                <div className="border border-gray-200 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setAppMode(v => !v)}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg"
                  >
                    <div className="text-left">
                      <span className="text-sm font-medium text-gray-700">Interactive app</span>
                      <p className="text-[11px] text-gray-500">Run a web UI (ComfyUI, Gradio, Jupyter) reachable via the exposed ports — no inference/predict. The Run Commands below start the app; idle autostop defaults to 90 min.</p>
                    </div>
                    <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ml-3 ${appMode ? 'bg-purple-600' : 'bg-gray-300'}`}>
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${appMode ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Exposed Ports</label>
                  <p className="text-[11px] text-gray-500 mb-2">
                    Reach a web UI (Gradio, ComfyUI) or open a raw TCP/UDP port on the machine, even without a public IP. Requires the tunnel server to be configured.
                  </p>
                  {exposedPorts.length > 0 && (
                    <div className="space-y-2 mb-2">
                      {exposedPorts.map((p, i) => {
                        const upd = (patch) => setExposedPorts(prev => prev.map((x, j) => j === i ? { ...x, ...patch } : x));
                        return (
                          <div key={i} className="grid grid-cols-12 gap-2 items-center">
                            <input type="number" placeholder="Port" value={p.port}
                              onChange={(e) => upd({ port: e.target.value })}
                              className="col-span-3 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                            <select value={p.protocol} onChange={(e) => upd({ protocol: e.target.value })}
                              className="col-span-2 border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white">
                              <option value="http">http</option>
                              <option value="https">https</option>
                              <option value="tcp">tcp</option>
                              <option value="udp">udp</option>
                            </select>
                            <input type="text" placeholder="name (e.g. comfyui)" value={p.name}
                              onChange={(e) => upd({ name: e.target.value })}
                              className="col-span-4 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                            <input type="text"
                              placeholder={(p.protocol === 'tcp' || p.protocol === 'udp') ? 'remote port (opt)' : 'user:pass (opt)'}
                              value={p.auth || ''}
                              onChange={(e) => upd({ auth: e.target.value })}
                              className="col-span-2 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                            <button type="button"
                              onClick={() => setExposedPorts(prev => prev.filter((_, j) => j !== i))}
                              className="col-span-1 text-gray-400 hover:text-red-500 text-sm" title="Remove">✕</button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <button type="button"
                    onClick={() => setExposedPorts(prev => [...prev, { port: '', protocol: 'http', name: '', auth: '' }])}
                    className="text-sm text-blue-600 hover:text-blue-700">+ Add port</button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Setup Commands
                  </label>
                  <textarea
                    value={setup}
                    onChange={(e) => setSetup(e.target.value)}
                    placeholder="pip install torch&#10;git clone https://github.com/..."
                    rows="4"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Run Commands *
                  </label>
                  <textarea
                    value={run}
                    onChange={(e) => setRun(e.target.value)}
                    placeholder="python train.py --epochs 100"
                    rows="3"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm"
                    required
                  />
                </div>
              </div>
            )}

            {/* Advanced Mode - YAML Editor */}
            {mode === 'advanced' && (
              <div className="space-y-4">
                {/* Template Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Load Template
                  </label>
                  <select
                    onChange={(e) => loadTemplate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    defaultValue=""
                  >
                    <option value="">-- Select a template --</option>
                    <option value="pytorch_training">PyTorch Training</option>
                    <option value="llm_inference">LLM Inference</option>
                    <option value="distributed_training">Distributed Training (Multi-Node)</option>
                    <option value="jupyter_notebook">Jupyter Notebook Server</option>
                  </select>
                </div>

                {/* YAML Editor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Task Configuration (YAML) *
                  </label>
                  <textarea
                    value={taskYaml}
                    onChange={(e) => setTaskYaml(e.target.value)}
                    placeholder="resources:&#10;  provider_type: both&#10;  min_cpu_cores: 8&#10;  min_ram_gb: 32&#10;  instance_type: spot&#10;  accelerators: V100:1&#10;&#10;run: |&#10;  python train.py"
                    rows="16"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm"
                    required
                  />
                </div>
              </div>
            )}

            {/* Cost estimate — the rate this cluster will actually be billed at */}
            {selectedGpu && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Estimated cost</span>
                  {estimateLoading && (
                    <RefreshCw size={12} className="text-gray-400 animate-spin" />
                  )}
                </div>

                {estimate ? (
                  <div className="px-4 py-3 space-y-3">
                    <div className="flex items-baseline justify-between">
                      <div>
                        <span className="text-2xl font-bold text-gray-900 font-mono">
                          {formatCents(estimate.total_rate_cents_per_hour)}
                        </span>
                        <span className="text-sm text-gray-500 ml-1">/hour</span>
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        {autoscaleEnabled ? (
                          <>
                            <div>{formatCents(estimate.total_rate_cents_per_hour * 24)} – {formatCents(estimate.rate_cents_per_hour * autoscaleMaxNodes * 24)} / day</div>
                            <div>{formatCents(estimate.total_rate_cents_per_hour * 24 * 30)} – {formatCents(estimate.rate_cents_per_hour * autoscaleMaxNodes * 24 * 30)} / month</div>
                          </>
                        ) : (
                          <>
                            <div>{formatCents(estimate.total_rate_cents_per_hour * 24)} / day</div>
                            <div>{formatCents(estimate.total_rate_cents_per_hour * 24 * 30)} / month</div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="text-xs text-gray-500 space-y-0.5">
                      <div>
                        {estimate.accelerator
                          ? `${estimate.accelerator_count}× ${estimate.accelerator}`
                          : [estimate.instance_type, estimate.cpus && `${estimate.cpus} vCPU`,
                             estimate.memory_gb && `${estimate.memory_gb} GB`]
                              .filter(Boolean).join(' · ') || 'CPU only'}
                        {estimate.num_nodes > 1 && ` · ${estimate.num_nodes} nodes`}
                        {autoscaleEnabled && ` · scales to ${autoscaleMaxNodes} nodes`}
                        {' · '}
                        {formatCents(estimate.rate_cents_per_hour)}/hr per node
                      </div>
                      <div>
                        {estimate.provider || 'cheapest provider'}
                        {estimate.region && ` · ${estimate.region}`}
                        {estimate.use_spot && ' · spot'}
                        {estimate.price_source && ` · ${PRICE_SOURCE_LABEL[estimate.price_source] || estimate.price_source}`}
                      </div>
                      {estimate.catalog_exact_match === false && (
                        <div className="text-amber-600">
                          No instance with exactly {estimate.accelerator_count} GPUs — estimated from per-GPU pricing.
                        </div>
                      )}
                      {estimate.spot_price_estimated && (
                        <div className="text-amber-600">
                          This provider does not publish real spot rates — the spot price is derived
                          from on-demand and may not reflect what you are charged.
                        </div>
                      )}
                    </div>

                    {/* Admin-only: what we pay vs. what we keep */}
                    {isAdmin && estimate.total_cloud_cost_cents_per_hour != null && (
                      <div className="flex items-center space-x-4 text-xs pt-2 border-t border-gray-100">
                        <span className="text-gray-500">
                          Infra cost{' '}
                          <span className="font-mono text-gray-700">
                            {formatCents(estimate.total_cloud_cost_cents_per_hour)}/hr
                          </span>
                        </span>
                        <span className="text-gray-500">
                          Margin{' '}
                          <span className="font-mono text-green-700">
                            {formatCents(estimate.total_markup_cents_per_hour)}/hr
                          </span>
                          {estimate.markup_percent != null && ` (${estimate.markup_percent}%)`}
                        </span>
                      </div>
                    )}

                    {/* Balance runway */}
                    {balanceCents != null && (
                      <div className={`text-xs rounded px-2 py-1.5 flex items-start space-x-1.5 ${
                        hoursRemaining != null && hoursRemaining < 1
                          ? 'bg-red-50 text-red-700'
                          : hoursRemaining != null && hoursRemaining < 6
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-gray-50 text-gray-600'
                      }`}>
                        {hoursRemaining != null && hoursRemaining < 6 && (
                          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                        )}
                        <span>
                          Balance {formatCents(balanceCents)}
                          {hoursRemaining != null && (
                            <> — covers about {hoursRemaining < 1
                              ? `${Math.round(hoursRemaining * 60)} minutes`
                              : `${hoursRemaining.toFixed(1)} hours`} at this rate.
                              {hoursRemaining < 1 && ' The cluster will be auto-stopped when credits run out.'}
                            </>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="px-4 py-3 text-xs text-gray-500">
                    {estimateLoading
                      ? 'Pricing this configuration...'
                      : estimateError || 'No price available for this configuration.'}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Launching...</span>
                </>
              ) : (
                <>
                  <Rocket size={16} />
                  <span>Launch Cluster</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LaunchClusterModal;
